import { Spinner, Text } from '@/ui-kit'
import { SCREEN_SIZE, useMediaQuery } from '@/ui-kit/media'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Mission } from './api/models'
import { MissionCard } from './components/MissionCard'
import { MissionsListEmptyState } from './components/MissionEmptyState'

function categorizeMissions(missions: Mission[]) {
  const inProgress: Mission[] = []
  const available: Mission[] = []
  const completed: Mission[] = []
  const unavailable: Mission[] = []

  for (const mission of missions) {
    if (!mission.availability.available) {
      unavailable.push(mission)
      continue
    }

    const { latestSubmissionStatus, nextSubmissionAvailable } = mission.view
    if (latestSubmissionStatus === 'IN_PROGRESS' || latestSubmissionStatus === 'SUBMITTED') {
      inProgress.push(mission)
    } else if (
      (latestSubmissionStatus === 'COMPLETED' || latestSubmissionStatus === 'FAILED') &&
      !nextSubmissionAvailable
    ) {
      completed.push(mission)
    } else if (nextSubmissionAvailable) {
      available.push(mission)
    }
  }

  return { inProgress, available, completed, unavailable }
}

const CARD_WIDTH_MOBILE = 320
const CARD_WIDTH = 382

export type MissionsProps = {
  availableMissions: Mission[]
  isInitialLoad?: boolean
}

export const Missions = ({ availableMissions, isInitialLoad = false }: MissionsProps) => {
  const [searchParams] = useSearchParams()
  const missionIdParam = searchParams.get('missionId')
  const [highlightMissionId, setHighlightMissionId] = useState<string | null>(null)

  const isMobile = useMediaQuery(SCREEN_SIZE.MOBILE)

  const {
    inProgress: inProgressMissions,
    available: availableToStartMissions,
    completed: completedMissions,
    unavailable: unavailableMissions
  } = useMemo(() => categorizeMissions(availableMissions), [availableMissions])

  useEffect(() => {
    if (!missionIdParam) {
      setHighlightMissionId(null)
      return
    }
    if (isInitialLoad) return
    if (!availableMissions.some((m) => m.id === missionIdParam)) return

    setHighlightMissionId(missionIdParam)
    const clearHighlight = window.setTimeout(() => setHighlightMissionId(null), 3000)
    const scrollToCard = () => {
      document.getElementById(`mission-card-${missionIdParam}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
    requestAnimationFrame(() => requestAnimationFrame(scrollToCard))

    return () => clearTimeout(clearHighlight)
  }, [isInitialLoad, missionIdParam, availableMissions])

  const gridStyle = {
    gridTemplateColumns: `repeat(auto-fill,${isMobile ? `max(${CARD_WIDTH_MOBILE}px, calc(100vw - 40px))` : `${CARD_WIDTH}px`})`
  }

  return (
    <div className="max-mobile:gap-6 flex flex-col gap-10">
      {isInitialLoad && (
        <div className="flex w-full justify-center">
          <Spinner className="mt-8" />
        </div>
      )}
      {!isInitialLoad && (
        <>
          {!availableMissions.length && <MissionsListEmptyState />}
          {!!inProgressMissions.length && (
            <div>
              <Text type="body2-regular" className="text-gray-80 mb-3 block">
                In Progress ({inProgressMissions.length})
              </Text>
              <ul className="grid gap-4" style={gridStyle}>
                {inProgressMissions.map((mission) => (
                  <MissionCard key={mission.id} mission={mission} highlighted={highlightMissionId === mission.id} />
                ))}
              </ul>
            </div>
          )}
          {!!availableToStartMissions.length && (
            <div>
              <Text type="body2-regular" className="text-gray-80 mb-3 block">
                Available ({availableToStartMissions.length})
              </Text>
              <ul className="grid gap-4" style={gridStyle}>
                {availableToStartMissions.map((mission) => (
                  <MissionCard key={mission.id} mission={mission} highlighted={highlightMissionId === mission.id} />
                ))}
              </ul>
            </div>
          )}
          {!!completedMissions.length && (
            <div>
              <Text type="body2-regular" className="text-gray-80 mb-3 block">
                Completed ({completedMissions.length})
              </Text>
              <ul className="grid gap-4" style={gridStyle}>
                {completedMissions.map((mission) => (
                  <MissionCard key={mission.id} mission={mission} highlighted={highlightMissionId === mission.id} />
                ))}
              </ul>
            </div>
          )}
          {!!unavailableMissions.length && (
            <div>
              <Text type="body2-regular" className="text-gray-80 mb-3 block">
                Unavailable ({unavailableMissions.length})
              </Text>
              <ul className="grid gap-4" style={gridStyle}>
                {unavailableMissions.map((mission) => (
                  <MissionCard key={mission.id} mission={mission} highlighted={highlightMissionId === mission.id} />
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}
